import * as Popover from "@radix-ui/react-popover";
import { ChevronDownIcon, Cross2Icon, MagnifyingGlassIcon } from "@radix-ui/react-icons";
import clsx from "clsx";
import { forwardRef, useMemo, useRef, useState, useLayoutEffect } from "react";

/** Lists longer than this get a search box (Core Industries, Organisation Types…). */
const SEARCH_THRESHOLD = 8;

export interface Option {
  label: string;
  value: string;
}

interface MultiSelectDropdownProps {
  className?: string;
  options: Option[];
  value: Option[];
  onChange: (selected: Option[]) => void;
  placeholder?: string;
  /** Singular label used in the summary count (e.g. "area" → "1 area selected", "2 areas selected") */
  itemLabel?: string;
  /** Show selected items as pill tags below the trigger (default: true) */
  showPills?: boolean;
  checkboxClassName?: string;
  checkboxIndicatorClassName?: string;
  /**
   * Show a search box above the options. Defaults to on for lists longer than
   * SEARCH_THRESHOLD, so long master-data lists are searchable everywhere
   * without each call site opting in; pass `false` to force it off.
   */
  searchable?: boolean;
}

export const MultiSelectDropdown = forwardRef<HTMLButtonElement, MultiSelectDropdownProps>(
  function MultiSelectDropdown(
    {
      className = "",
      options,
      value,
      onChange,
      placeholder = "Select option",
      itemLabel = "item",
      showPills = true,
      checkboxClassName = "",
      checkboxIndicatorClassName = "",
      searchable,
    },
    ref,
  ) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const showSearch = searchable ?? options.length > SEARCH_THRESHOLD;
    const visibleOptions = useMemo(() => {
      const q = query.trim().toLowerCase();
      return q ? options.filter((o) => o.label.toLowerCase().includes(q)) : options;
    }, [options, query]);

    const isSelected = (val: string) => value.some((v) => v.value === val);

    const toggleValue = (option: Option) => {
      onChange(
        isSelected(option.value)
          ? value.filter((v) => v.value !== option.value)
          : [...value, option],
      );
    };

    const removePill = (option: Option, e: React.MouseEvent) => {
      e.stopPropagation();
      onChange(value.filter((v) => v.value !== option.value));
    };

    const summaryText =
      value.length === 0
        ? placeholder
        : value.length === options.length
          ? "All selected"
          : `${value.length} ${value.length === 1 ? itemLabel : `${itemLabel}s`} selected`;

    const triggerRef = useRef<HTMLButtonElement | null>(null);
    const [triggerWidth, setTriggerWidth] = useState(0);

    useLayoutEffect(() => {
      if (triggerRef.current) {
        setTriggerWidth(triggerRef.current.offsetWidth);
      }
    }, []);

    return (
      <div className="flex flex-col gap-3">
        <Popover.Root
          open={open}
          onOpenChange={(next) => {
            setOpen(next);
            // A fresh list each time it opens, not the last search.
            if (!next) setQuery("");
          }}
        >
          <Popover.Trigger asChild>
            <button
              ref={(node) => {
                triggerRef.current = node;
                if (typeof ref === "function") ref(node);
                else if (ref) ref.current = node;
              }}
              type="button"
              role="combobox"
              aria-expanded={open}
              className={clsx(
                "w-full h-[54px] flex items-center justify-between px-4",
                "rounded-[14px] border border-theme-border bg-[var(--theme-input-bg)]",
                "text-16 transition-colors duration-200",
                "focus:outline-none focus:border-primary-350 focus:ring-1 focus:ring-primary-350",
                open && "border-primary-350 ring-1 ring-primary-350",
                className,
              )}
            >
              <span
                className={clsx(
                  "text-16",
                  value.length === 0
                    ? "text-[var(--theme-input-placeholder)]"
                    : "text-theme-text-heading",
                )}
              >
                {summaryText}
              </span>
              <ChevronDownIcon
                width={16}
                height={16}
                className={clsx(
                  "text-theme-text-secondary transition-transform duration-200 flex-shrink-0",
                  open && "rotate-180",
                )}
                aria-hidden="true"
              />
            </button>
          </Popover.Trigger>

          <Popover.Portal>
            <Popover.Content
              align="start"
              sideOffset={6}
              style={{ width: triggerWidth || "auto" }}
              // With a search box, focus goes to it rather than the panel.
              onOpenAutoFocus={showSearch ? (e) => e.preventDefault() : undefined}
              className="bg-theme-bg-surface border border-theme-border rounded-[14px] shadow-[0px_20px_25px_0px_rgba(0,0,0,0.1),0px_8px_10px_0px_rgba(0,0,0,0.1)] max-h-60 overflow-y-auto outline-none z-50 overflow-clip"
            >
              {showSearch && (
                // Sticky, so it stays put while the list scrolls under it.
                <div className="sticky top-0 z-10 flex items-center gap-2 border-b border-theme-border-subtle bg-theme-bg-surface px-3">
                  <MagnifyingGlassIcon
                    className="h-4 w-4 flex-shrink-0 text-theme-text-muted"
                    aria-hidden="true"
                  />
                  <input
                    autoFocus
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search…"
                    aria-label={`Search ${placeholder}`}
                    className="h-10 w-full min-w-0 bg-transparent text-14 text-theme-text placeholder:text-[var(--theme-input-placeholder)] focus:outline-none"
                  />
                </div>
              )}
              {showSearch && visibleOptions.length === 0 && (
                <p className="px-4 py-3 text-14 text-theme-text-muted">
                  No matches for “{query.trim()}”
                </p>
              )}
              {visibleOptions.map((opt) => {
                const selected = isSelected(opt.value);
                return (
                  <div
                    key={opt.value}
                    onClick={() => toggleValue(opt)}
                    role="option"
                    aria-selected={selected}
                    className={clsx(
                      "flex items-center gap-[7px] h-9 px-4 cursor-pointer transition-colors border",
                      selected
                        ? "bg-okt-selected-bg border-okt-selected-border"
                        : "border-transparent hover:bg-okt-selected-bg",
                    )}
                  >
                    {/* Figma-style checkbox */}
                    <div
                      className={clsx(
                        "flex-shrink-0 w-[15px] h-[15px] rounded-[4px] flex items-center justify-center transition-colors",
                        selected
                          ? "bg-goals-purple-selected"
                          : "border border-theme-border bg-theme-bg-surface",
                        checkboxClassName,
                      )}
                    >
                      {selected && (
                        <svg
                          width="9"
                          height="7"
                          viewBox="0 0 9 7"
                          fill="none"
                          className={checkboxIndicatorClassName}
                        >
                          <path
                            d="M1 3.5L3.5 6L8 1"
                            stroke="white"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </div>
                    <span
                      className={clsx(
                        "text-14 font-medium leading-5 truncate",
                        selected ? "text-goals-purple-selected" : "text-theme-text",
                      )}
                    >
                      {opt.label}
                    </span>
                  </div>
                );
              })}
            </Popover.Content>
          </Popover.Portal>
        </Popover.Root>

        {/* Selected pills */}
        {showPills && value.length > 0 && (
          <div className="flex flex-wrap gap-[10px]">
            {value.map((v) => (
              <span
                key={v.value}
                className="inline-flex items-center gap-[10px] px-3 py-[5px] rounded-full bg-okt-selected-bg border border-okt-selected-border"
              >
                <span className="text-12 font-medium text-goals-purple-selected leading-4">
                  {v.label}
                </span>
                <button
                  type="button"
                  onClick={(e) => removePill(v, e)}
                  className="opacity-60 hover:opacity-100 transition-opacity flex-shrink-0"
                  aria-label={`Remove ${v.label}`}
                >
                  <Cross2Icon width={11} height={11} className="text-goals-purple-selected" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>
    );
  },
);
